import {combineLatest} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {numUtil} from '@/helper/num';
import {spotsCache$} from '@/config';
import {trades$} from '@/trades/trades';
import {spotMarketCache$} from './market24h-spot-cache';
import type {TradeData, SpotMarket, SpotPair} from '@/types';

const spotTrade$ = trades$.pipe(filter((trades: TradeData) => trades.tradeType === 'Spot'));

export const spotMarket24hRealtime$ = combineLatest([spotsCache$, spotTrade$, spotMarketCache$]).pipe(
  map(convertSpotMarket24h),
  filter(Boolean)
);

function convertSpotMarket24h([spotsCache, {symbol, trades}, cache]: [
  Map<string, SpotPair>,
  TradeData,
  Map<string, SpotMarket>
]): SpotMarket {
  const spot = spotsCache.get(symbol);
  if (!spot || !cache.has(symbol)) {
    return null;
  }
  const {pricePrecision, quoteQtyScale: priceScale, urlSymbol} = spot;
  const item = cache.get(symbol);
  const closeEp = trades.length > 0 ? trades[0].priceEp : item.closeEp;
  const close = numUtil.delimit(numUtil.floor(closeEp, pricePrecision, priceScale));
  const change24hEp = closeEp !== 0 && item.openEp !== 0 ? closeEp - item.openEp : 0;
  const change24h = numUtil.floor(change24hEp, pricePrecision, priceScale);
  const change24hStr = gen24hChangeStr(change24hEp, change24h);
  const changePercent = genChangePercent(closeEp, item.openEp);
  const changePercentStr = genChangePercentStr(item.changePercent);
  return {...item, closeEp, close, change24hEp, change24hStr, changePercent, changePercentStr, urlSymbol};
}

function gen24hChangeStr(change24hEp, change24h) {
  const prefix = change24hEp > 0 ? '+' : '';
  return prefix + change24h;
}

function genChangePercent(closeEp, openEp) {
  if (closeEp === 0 || openEp === 0 || closeEp === openEp) {
    return 0;
  }
  const changeEp = closeEp - openEp;
  return (changeEp * 100) / openEp;
}

function genChangePercentStr(changePercent) {
  if (changePercent === 0) {
    return '0.00%';
  }
  const prefix = changePercent > 0 ? '+' : '';
  const percent = numUtil.floor(changePercent, 2);
  return prefix + percent + '%';
}
